import {Serializable} from '../interface/serializable.interface';
import {Selectable} from '../interface/selectable.interface';
import {StringJoiner} from '../joiner/string.joiner';
import {Address, AddressList} from './address.model';
import {IAddressList} from './interface/address.model.interface';
import {IObjectList, ObjectList} from './interface/object-list.model.interface';
import {IPhone, PhoneList} from './interface/phone.model.interface';
import {AddressType} from './interface/address-type.model.interface';
import {ISimpleSelectableData} from './interface/simple-selectable-data.model.interface';
import {EmailList, IEmail} from './interface/email.model.interface';
import {StringBuilder} from 'typescript-string-operations';
import {ILessorGroup} from "./lessor-group.model";
import {ICustomerVerification} from "./interface/customer-verification.interface";
import {ICustomerExecutionRemark} from "./interface/customer-remark.interface";

export type CompanyLegalForm = 'STANDARD' | 'CIVIL_PARTNERSHIP';

export interface ICivilPartnershipPartner {
  id: number;
  companyName?: string;
  companyType?: 'LEGAL_ENTITY' | 'NATURAL_PERSON';
  name: string;
  secondName: string;
  surname: string;
  pesel: string;
}

export enum LegalEntityType {
  COMPANY = ('COMPANY'),
  COMPANY_TO_BE_VERIFIED = ('COMPANY_TO_BE_VERIFIED'),
  NATURAL_PERSON = ('NATURAL_PERSON'),
  NATURAL_PERSON_TO_BE_VERIFIED = ('NATURAL_PERSON_TO_BE_VERIFIED')
}

export interface ILegalEntity {
  type: LegalEntityType;
  active: boolean;
  id?: number;
  name?: string;
  phones: IObjectList<IPhone>;
  emails: IObjectList<IEmail>;
  secondName?: string;
  surname?: string;
  pesel?: string;
  companyName?: string;
  nip?: string;
  regon?: string;
  krs?: string;
  addresses: IAddressList;
  individualNumber?: string;
  group?: ILessorGroup;
  coreId: number;
  lessorId?: number;
  legalForm?: CompanyLegalForm;
  civilPartnershipPartners?: ICivilPartnershipPartner[];
  dead: boolean;
  dataConsistentWithPeselDatabase: boolean;
  verificationErrorMessage: string;
  verifications : IObjectList<ICustomerVerification>;
  hasExecutionRemarks: boolean;
  executionRemarks: IObjectList<ICustomerExecutionRemark>;
}

export abstract class LegalEntity implements Serializable, Selectable {
  private readonly _id: number;
  private readonly _individualNumber: string;
  private readonly _addresses: AddressList;
  private readonly _phones: PhoneList;
  private readonly _emails: EmailList;
  private _active: boolean;
  protected _type: LegalEntityType;
  private _coreId: number;
  private readonly _lessorId: number;
  private _verifications: IObjectList<ICustomerVerification>
  private _hasExecutionRemarks: boolean;
  private _executionRemarks: IObjectList<ICustomerExecutionRemark>;

  protected constructor(legalEntity: ILegalEntity) {
    this._id = legalEntity.id;
    this._type = legalEntity.type;
    this._individualNumber = legalEntity.individualNumber;
    if (legalEntity.emails) {
      this._emails = new EmailList(legalEntity.emails);
    }
    if (legalEntity.phones) {
      this._phones = new PhoneList(legalEntity.phones);
    }
    if (legalEntity.addresses) {
      this._addresses = new AddressList(legalEntity.addresses);
    }
    this._active = legalEntity.active;
    this._coreId = legalEntity.coreId
    this._lessorId = legalEntity.lessorId;
    this._verifications = legalEntity.verifications
    this._hasExecutionRemarks = legalEntity.hasExecutionRemarks
    this._executionRemarks = legalEntity.executionRemarks
  }

  get individualNumber(): string {
    return this._individualNumber;
  }

  get id(): number {
    return this._id;
  }

  get coreId(): number {
    return this._coreId
  }

  get lessorId(): number {
    return this._lessorId;
  }

  get type(): LegalEntityType {
    return this._type;
  }

  get active(): boolean {
    return this._active;
  }

  set active(active) {
    this._active = active;
  }

  get verifications() {
    return this._verifications
  }

  get executionsRemarks() {
    return this._executionRemarks;
  }

  toText(): string {
    const joiner: StringJoiner = new StringJoiner('\n');
    if (this._individualNumber) {
      joiner.append(`Nr klienta: ${this._individualNumber}`);
    }
    joiner.append(`Email: ${this._emails.data.map(el => el.email).join(', ')}`);
    joiner.append(`Telefon: ${this._phones.data.map(el => el.phone).join(', ')}`);
    joiner.append(`${this.getAddresses()}`);
    return joiner.join();
  }

  getAddresses(): string {
    return this._addresses?.data
      .map(address => address.toText())
      .join('\n');
  }

  getEmails(): string[] {
    return this._emails.data.map(el => el.email);
  }

  get addresses(): AddressList {
    return this._addresses;
  }

  get phones(): PhoneList {
    return this._phones;
  }

  get emails(): EmailList {
    return this._emails;
  }

  get mainAddress(): Address {
    return this._addresses.data
      .find(address => address.type.type === AddressType.MAIN);
  }

  getPhones() {
    return this._phones.data.map(el => el.phone);
  }

  abstract toSelectText(): string;

  toHTML(): string {
    const joiner: StringJoiner = new StringJoiner('<br>');
    if (this._individualNumber) {
      joiner.append(`Nr klienta: <strong>${this._individualNumber}</strong>`);
    }
    joiner.append(`Email: ${this._emails.data.map(el => el.email).join(', ')}`);
    joiner.append(`Telefon: ${this._phones.data.map(el => el.phone).join(', ')}`);
    joiner.append(`${this._addresses.data.map(el => this.addressToHtml(el)).join('<br>')}`);
    return joiner.join();
  }

  toPreview(): string {
    const joiner: StringJoiner = new StringJoiner('<br>');
    if (this._individualNumber) {
      joiner.append(`<strong>${this._individualNumber}</strong>`);
    }
    return joiner.join();
  }

  toAddressPreview(): string {
    return this._addresses.data.map(el => this.addressToHtml(el)).join('<br>');
  }

  toContractPreview(): string {
    const joiner: StringJoiner = new StringJoiner('<br>');
    joiner.append(`${this._emails.data.map(el => el.email).join(', ')}`);
    joiner.append(`${this._phones.data.map(el => el.phone).join(', ')}`);
    return joiner.join();
  }

  addressToHtml(address: Address): string {
    const builder: StringBuilder = new StringBuilder();

    if (address.verificationErrorMessage != null) {
      builder.append('<span class="verificationError">')
      builder.append(`<strong>${address.verificationErrorMessage}</strong>`)
      builder.append("</span><br>")
    }

    if (address.addressCorrect == false) {
      builder.append('<span class="incorrectVerification">')
      this.appendAddress(builder, address);
      return builder.toString();
    } else if(address.addressCorrect == true) {
      builder.append('<span class="correctVerification">')
      this.appendAddress(builder, address);
      return builder.toString();
    } else {
      builder.append('<span>')
      this.appendAddress(builder, address);
      builder.append('</span>')
      return builder.toString();
    }
  }

  private appendAddress(builder: StringBuilder, address: Address) {
    builder.append(`<strong>${address.type.label}</strong>: <br>`);
    if (address.street) {
      builder.appendFormat('{0} {1}', address.street, address.estateNumber);
    } else {
      builder.appendFormat('{0} {1}', address.city, address.estateNumber);
    }
    if (address.localNumber) {
      builder.appendFormat('/{0}', address.localNumber);
    }
    builder.appendFormat(',<br>{0} {1}', address.postalCode, address.city);
    builder.append('</span>')
  }
}


export class LegalEntityList extends ObjectList<LegalEntity, LegalEntity> {
  protected mapToListElement(instance: LegalEntity): LegalEntity {
    return instance;
  }

  protected mapToSelectableItem(instance: LegalEntity): ISimpleSelectableData {
    return {
      label: instance.toSelectText(),
      value: instance.id
    };
  }
}
